import { configureApiCalls } from "Api/Api";
import { Layout } from "Components/Layout/Layout";
import { ModuleLoadingFallback } from "Components/Layout/ModuleLoadingFallback";
import { ProfileCompletionModal } from "Components/ProfileCompletion/ProfileCompletionModal";
import { type FetchResponse, setupClient } from "Infrastructure/Api/Api";
import { ChatPushBridge } from "Infrastructure/Capacitor/ChatPushBridge";
import { NativeNavigationBridge } from "Infrastructure/Capacitor/NativeNavigationBridge";
import { isNativePlatform } from "Utils/Platform/PlatformUtils";
import { Routes } from "Routes/Routes";
import { extendSignOutDate, resetUser } from "State/Auth/AuthReducer";
import { getUserInfoAsync } from "State/Auth/UserInfo/GetUserInfoState";
import appStore, { type RootStateType } from "State/Store";
import { CzechResourcesDictionary } from "Translations/CzechResourcesDictionary";
import { EnglishResourcesDictionary } from "Translations/EnglishResourcesDictionary";
import { initializeAppLanguage } from "Translations/languageUtils";
import { SlovakResourcesDictionary } from "Translations/SlovakResourcesDictionary";
import { VietnameseResourcesDictionary } from "Translations/VietnameseResourcesDictionary";
import { JWT_KEY } from "Utils/AuthUtils";
import { getLocalStorageItem } from "Utils/Storage/LocalStorage/SecureLocalStorage";
import { trackUserIdentification } from "Utils/TrackingUtils";
import isPropValid from "@emotion/is-prop-valid";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { GoogleOAuthProvider } from "@react-oauth/google";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "Infrastructure/ReactQuery/queryClient";
import { prefetchUserInfo } from "Auth/Api/Queries/useUserInfoQuery";
// import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { cs } from "date-fns/locale/cs";
import i18n from "i18next";
import { Suspense, useEffect, useState } from "react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { initReactI18next } from "react-i18next";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";
import { StyleSheetManager } from "styled-components";
import * as z from "zod";
import {
  disableMixpanel,
  initMixpanel,
} from "Infrastructure/Utils/MixpanelUtils";
import { DisabledMixpanelIndicator } from "Components/Shared/DisabledMixpanelIndicator/DisabledMixpanelIndicator";
import { useKeyboardSequenceToggle } from "Hooks/useKeyboardSequenceToggle";
import { useAuthStateSync } from "Hooks/useAuthStateSync";
import { useSignalRNotifications } from "Hooks/useSignalRNotifications";
import { useSocialGraphSignalR } from "Hooks/Social/useSocialGraphSignalR";
import {
  LANGUAGE_TO_ZOD_LOCALE_MAP,
  type Language,
} from "Translations/languageConfig";
import { zodCustomErrorMap } from "Translations/zodCustomErrorMap";

i18n
  .use(initReactI18next) // passes i18n down to react-i18next
  .init({
    resources: {
      cs: CzechResourcesDictionary,
      en: EnglishResourcesDictionary,
      sk: SlovakResourcesDictionary,
      vi: VietnameseResourcesDictionary,
    },
    lng: initializeAppLanguage(),
    fallbackLng: "cs",
    interpolation: {
      escapeValue: false,
      defaultVariables: { appName: "Mo.one" },
    },
  });

// Load Zod locale based on app language
const loadZodLocale = async (language: Language) => {
  const zodLocale = LANGUAGE_TO_ZOD_LOCALE_MAP[language] || "en";
  z.config({
    ...z.locales[zodLocale](),
    customError: zodCustomErrorMap,
  });
};

loadZodLocale(initializeAppLanguage());

// AppContent component that uses Redux hooks
const AppContent = () => {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

  // Initialize cross-tab authentication synchronization
  useAuthStateSync({ showNotifications: true });
  // Initialize SignalR notifications
  useSignalRNotifications();
  // Live refresh of the social graph caches when the OTHER side acts
  useSocialGraphSignalR();

  const [wasMixpanelDisabled, setWasMixpanelDisabled] = useState(false);
  const shouldDisableMixpanel = useKeyboardSequenceToggle("togglemixpanel");

  // biome-ignore lint/correctness/useExhaustiveDependencies: Walkaround solution to make the effect not react to changes of wasMixpanelDisabled
  useEffect(() => {
    if (shouldDisableMixpanel) {
      disableMixpanel();
      setWasMixpanelDisabled(true);
    } else if (wasMixpanelDisabled) {
      initMixpanel();
    }
  }, [shouldDisableMixpanel]);

  useEffect(() => {
    const store = appStore.store.getState() as Partial<RootStateType>;
    const hasToken = !!getLocalStorageItem(JWT_KEY);

    // Redux fetch (keep for backwards compatibility during migration)
    if (store?.auth?.user && !store?.auth?.isUserFetching) {
      appStore.store.dispatch(
        getUserInfoAsync.request({ withLoadingStatus: true }),
      );
    }
    if (hasToken) {
      prefetchUserInfo(queryClient); // React Query prefetch (for new consumers using useUserInfoQuery)
    }
  }, []);

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey="6Lda2ZciAAAAAGjaHbO8SFHLUWxI31AGLbNl3M11"
      useEnterprise={true}
      scriptProps={{
        async: true,
        defer: true,
        appendTo: "body",
        nonce: undefined,
      }}
    >
      <GoogleOAuthProvider clientId={clientId}>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={cs}
          localeText={{
            fieldDayPlaceholder: () => "dd",
            fieldMonthPlaceholder: () => "mm",
            fieldYearPlaceholder: () => "yyyy",
          }}
        >
          {/*StyleSheetManager can be removed when all html styled components use transient props (starting with $), or style react components instead (styled.div -> styled(Box) https://styled-components.com/docs/faqs#shouldforwardprop-is-no-longer-provided-by-default*/}
          <StyleSheetManager shouldForwardProp={shouldForwardProp}>
            <BrowserRouter>
              <NativeNavigationBridge />
              {isNativePlatform() && <ChatPushBridge />}
              <Layout>
                <Suspense fallback={<ModuleLoadingFallback />}>
                  <Routes />
                </Suspense>
                <ProfileCompletionModal />
              </Layout>
              <DisabledMixpanelIndicator isVisible={shouldDisableMixpanel} />
            </BrowserRouter>
          </StyleSheetManager>
        </LocalizationProvider>
      </GoogleOAuthProvider>
    </GoogleReCaptchaProvider>
  );
};

const logoutUser = () => {
  appStore.store.dispatch(
    resetUser({
      onAfterReset: () => {
        window.setTimeout(() => window.location.reload(), 500);
      },
    }),
  );
};

const responseMiddleware = {
  name: "auth-middleware",
  fn: <TData, TStatus extends number>(
    response: FetchResponse<TData, TStatus>,
  ): FetchResponse<TData, TStatus> | null => {
    if (response.status === 401) {
      // Only logout if user has a token (is authenticated), Anonymous users getting 401 is expected and shouldn't trigger logout
      const token = localStorage.getItem(JWT_KEY);
      if (token) {
        logoutUser();
      }
    } else {
      trackUserIdentification();
    }
    return response;
  },
};

setupClient({
  apiUrl: import.meta.env.VITE_API_URL,
  jwtKey: JWT_KEY,
  responseMiddlewares: [responseMiddleware],
});

function App() {
  configureApiCalls({
    jwtKey: JWT_KEY,
    onResponse: (r) => {
      if (
        r.status !== 200 &&
        r.status !== 201 &&
        r.status !== 401 &&
        r.status !== 503
      ) {
        Sentry.captureEvent({
          message: `Problem while calling API ${JSON.stringify(r)}`,
          level: "log",
        });
      }
      if (r.status === 401) {
        logoutUser();
      } else {
        appStore.store.dispatch(extendSignOutDate());
        trackUserIdentification();
      }
    },
  });

  return (
    <QueryClientProvider client={queryClient}>
      {/* <ReactQueryDevtools initialIsOpen={false} /> */}
      <Provider store={appStore.store}>
        <PersistGate loading={null} persistor={appStore.persistor}>
          <AppContent />
        </PersistGate>
      </Provider>
    </QueryClientProvider>
  );
}

export default App;

// This implements the default behavior from styled-components v5
function shouldForwardProp(propName: any, target: any) {
  if (typeof target === "string") {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(propName);
  }
  // For other elements, forward all props
  return true;
}
